import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { useApolloClient } from "@apollo/client/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Building2, 
  User, 
  Calendar, 
  Shield,
  LogOut,
  ArrowRight
} from "lucide-react";
import { VALIDATE_INVITATION } from "@/graphql/queries";
import { ACCEPT_INVITATION, AUTHENTICATE_WITH_GOOGLE } from "@/graphql/mutations";
import { GET_CURRENT_USER } from "@/graphql/queries";
import { setAuthCookie, removeAuthCookie } from "@/lib/cookie-utils";
import { isImpersonating, setScopedStorageItem } from "@/lib/session-utils";
import { clearTokens, getAccessToken, saveTokens } from "@/lib/token-utils";

// Google Identity Services types
interface GoogleConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
}

interface GoogleButtonConfig {
  theme?: string;
  size?: string;
  width?: string;
  text?: string;
  locale?: string;
  type?: string;
}

interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: GoogleConfig) => void;
      renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
      prompt: (notification?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
    };
  };
}

declare const google: GoogleIdentity | undefined;

type PageState = "loading" | "invalid" | "guest" | "authenticated" | "mismatch" | "success" | "accepting";

// GraphQL response types
interface ValidateInvitationResponse {
  validateInvitation: {
    valid: boolean;
    email?: string;
    organizationName?: string;
    inviterName?: string;
    role?: string;
    expiresAt?: string;
    error?: string;
  };
}

interface GetCurrentUserResponse {
  me: {
    id: string;
    email: string;
    name?: string;
    avatarURL?: string;
    onboardingCompleted: boolean;
  };
}

interface AcceptInvitationResponse {
  acceptInvitation: {
    success: boolean;
    organization?: {
      id: string;
      name: string;
      role: string;
      isPersonal: boolean;
    };
    error?: string;
  };
}

interface AuthenticateWithGoogleResponse {
  authenticateWithGoogle: {
    success: boolean;
    user?: {
      id: string;
      email: string;
      name?: string;
      avatarURL?: string;
      onboardingCompleted: boolean;
    };
    organizations?: Array<{
      id: string;
      name: string;
      role: string;
    }>;
    tokens?: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    };
    requiresOnboarding: boolean;
    error?: string;
  };
}

const InviteAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [isGoogleInitialized, setIsGoogleInitialized] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  // Ref to track successful acceptance synchronously (not affected by React batching)
  const acceptedSuccessfullyRef = useRef(false);

  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    "787453514477-33m2jvargdpl27nkg8na1e4dmnq67f82.apps.googleusercontent.com";

  // Check if user is logged in
  const authToken = getAccessToken();
  
  // Query current user if logged in
  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserResponse>(GET_CURRENT_USER, {
    skip: !authToken,
    errorPolicy: 'all',
  });

  // Validate invitation - skip if we've already successfully accepted
  const { data: inviteData, loading: inviteLoading, error: inviteError } = useQuery<ValidateInvitationResponse>(VALIDATE_INVITATION, {
    variables: { token },
    skip: !token || pageState === "success",
    errorPolicy: 'all',
  });

  // Accept invitation mutation (for logged-in users)
  const [acceptInvitation, { loading: accepting }] = useMutation<AcceptInvitationResponse>(ACCEPT_INVITATION, {
    onCompleted: async (data) => {
      if (data.acceptInvitation.success) {
        // Set ref synchronously BEFORE any async operations to prevent race condition
        acceptedSuccessfullyRef.current = true;
        setPageState("success");
        setScopedStorageItem('showOrgSwitcherTooltip', 'true');
        await apolloClient.resetStore();
      } else {
        setError(data.acceptInvitation.error || "Failed to accept invitation");
        setPageState("authenticated");
      }
    },
    onError: (err) => {
      setError(err.message || "Failed to accept invitation");
      setPageState("authenticated");
    },
  });

  // Auth with Google mutation (includes invitation token)
  const [authenticateWithGoogle, { loading: authenticating }] = useMutation<AuthenticateWithGoogleResponse>(AUTHENTICATE_WITH_GOOGLE, {
    onCompleted: async (data) => {
      if (data.authenticateWithGoogle.success) {
        if (data.authenticateWithGoogle.tokens) {
          saveTokens(data.authenticateWithGoogle.tokens.accessToken, data.authenticateWithGoogle.tokens.refreshToken);
          setAuthCookie(data.authenticateWithGoogle.tokens.accessToken);
          acceptedSuccessfullyRef.current = true;
          setPageState("success");
          setScopedStorageItem('showOrgSwitcherTooltip', 'true');
          await apolloClient.resetStore();
        } else if (data.authenticateWithGoogle.requiresOnboarding) {
          sessionStorage.setItem('pendingGoogleIdToken', sessionStorage.getItem('inviteGoogleIdToken') || '');
          navigate("/onboarding");
        }
      } else {
        setError(data.authenticateWithGoogle.error || "Authentication failed");
      }
    },
    onError: (err) => {
      setError(err.message || "Authentication failed");
    },
  });

  // Handle Google credential response
  const handleCredentialResponse = useCallback(async (response: { credential: string }) => {
    if (!response || !response.credential) {
      setError("No credential received from Google");
      return;
    }

    // Store for potential use
    sessionStorage.setItem('inviteGoogleIdToken', response.credential);

    // Authenticate with invitation token
    await authenticateWithGoogle({
      variables: {
        input: {
          idToken: response.credential,
          invitationToken: token,
        },
      },
    });
  }, [authenticateWithGoogle, token]);

  // Initialize Google Sign-In for guest users
  useEffect(() => {
    if (pageState !== "guest" || isGoogleInitialized) return;

    const initializeGoogleSignIn = () => {
      if (typeof google === 'undefined' || !google?.accounts?.id) {
        console.error("Google Identity Services not loaded");
        return;
      }

      try {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
        });

        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = '';
          google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            width: "400",
            text: "signin_with",
            locale: "en",
            type: "standard",
          });
        }

        setIsGoogleInitialized(true);
      } catch (err) {
        console.error("Failed to initialize Google Sign-In:", err);
      }
    };

    if (typeof google !== 'undefined' && google?.accounts?.id) {
      initializeGoogleSignIn();
    } else {
      const checkGoogle = setInterval(() => {
        if (typeof google !== 'undefined' && google?.accounts?.id) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);

      return () => clearInterval(checkGoogle);
    }
  }, [pageState, isGoogleInitialized, GOOGLE_CLIENT_ID, handleCredentialResponse]);

  // Determine page state based on query results
  useEffect(() => {
    // Don't override success state - check both React state AND ref (ref is synchronous, state may be batched)
    if (pageState === "success" || acceptedSuccessfullyRef.current) {
      // Ensure state is consistent with ref
      if (pageState !== "success" && acceptedSuccessfullyRef.current) {
        setPageState("success");
      }
      return;
    }

    if (inviteLoading || userLoading) {
      setPageState("loading");
      return;
    }

    if (inviteError || !inviteData?.validateInvitation?.valid) {
      setError(inviteData?.validateInvitation?.error || "This invitation is invalid or has expired");
      setPageState("invalid");
      return;
    }

    const invitation = inviteData.validateInvitation;
    const currentUser = userData?.me;

    if (!authToken || !currentUser) {
      // User not logged in
      setPageState("guest");
      return;
    }

    // User is logged in - check email match
    if (currentUser.email?.toLowerCase() === invitation.email?.toLowerCase()) {
      setPageState("authenticated");
    } else {
      setPageState("mismatch");
    }
  }, [inviteLoading, userLoading, inviteData, userData, authToken, inviteError, pageState]);

  // Success countdown
  useEffect(() => {
    if (pageState === "success" && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (pageState === "success" && countdown === 0) {
      navigate("/");
    }
  }, [pageState, countdown, navigate]);

  // Handle accept invitation (for logged-in users)
  const handleAccept = async () => {
    if (!token) return;
    setPageState("accepting");
    await acceptInvitation({ variables: { token } });
  };

  // Handle sign out
  const handleSignOut = () => {
    clearTokens();
    if (!isImpersonating()) {
      removeAuthCookie();
    }
    window.location.reload();
  };

  const invitation = inviteData?.validateInvitation;
  const currentUser = userData?.me;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/Logo White Transparent.png"
              alt="FlowLabs Logo"
              className="h-16 w-auto"
            />
          </div>
        </div>

        {/* Loading State */}
        {pageState === "loading" && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating invitation...</p>
            </div>
          </Card>
        )}

        {/* Invalid/Expired State */}
        {pageState === "invalid" && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Invitation Invalid</h2>
                <p className="text-muted-foreground">
                  {error || "This invitation has expired or is no longer valid."}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Contact the person who invited you to request a new invitation.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Go to Login</Link>
              </Button>
            </div>
          </Card>
        )}

        {/* Guest State - Not Logged In */}
        {pageState === "guest" && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">You've been invited!</h2>
                <p className="text-muted-foreground">
                  Join <span className="font-semibold text-foreground">{invitation.organizationName}</span> on FlowLabs
                </p>
              </div>

              <div className="space-y-3 py-4 border-y border-border">
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="font-medium">{invitation.organizationName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Role:</span>
                  <span className="font-medium capitalize">{invitation.role?.toLowerCase()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Invited by:</span>
                  <span className="font-medium">{invitation.inviterName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Expires:</span>
                  <span className="font-medium">{invitation.expiresAt}</span>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                {/* Hidden Google button that handles the click */}
                <div
                  ref={googleButtonRef}
                  className="absolute opacity-0 pointer-events-auto z-10"
                  style={{ minHeight: '56px', width: '100%' }}
                />
                
                {/* Visible styled button */}
                <div className="relative">
                  <Button
                    disabled={authenticating || !isGoogleInitialized}
                    className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90"
                    size="lg"
                    onClick={() => {
                      // Click the hidden Google button
                      const googleBtn = googleButtonRef.current?.querySelector('div[role="button"]') as HTMLElement;
                      googleBtn?.click();
                    }}
                  >
                    {authenticating ? (
                      <>
                        <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Sign in to Accept
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-center text-muted-foreground">
                  Don't have an account? You'll create one when you sign in.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Authenticated State - Email Matches */}
        {(pageState === "authenticated" || pageState === "accepting") && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Accept Invitation</h2>
                <p className="text-muted-foreground">
                  Join <span className="font-semibold text-foreground">{invitation.organizationName}</span>
                </p>
              </div>

              <div className="space-y-3 py-4 border-y border-border">
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="font-medium">{invitation.organizationName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Role:</span>
                  <span className="font-medium capitalize">{invitation.role?.toLowerCase()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Invited by:</span>
                  <span className="font-medium">{invitation.inviterName}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">Signed in as:</span>
                <span className="text-sm font-medium">{currentUser?.email}</span>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate("/")}
                  disabled={pageState === "accepting"}
                >
                  Decline
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAccept}
                  disabled={pageState === "accepting"}
                >
                  {pageState === "accepting" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      Accept Invitation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Email Mismatch State */}
        {pageState === "mismatch" && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <div className="space-y-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-yellow-500" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold">Email Mismatch</h2>
                  <p className="text-muted-foreground">
                    This invitation was sent to a different email address.
                  </p>
                </div>
              </div>

              <div className="space-y-3 py-4 border-y border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Invitation for:</span>
                  <span className="font-medium">{invitation.email}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">You're signed in as:</span>
                  <span className="font-medium">{currentUser?.email}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleSignOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out and use correct account
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate("/")}
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Success State */}
        {pageState === "success" && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Welcome to {invitation.organizationName}!</h2>
                <p className="text-muted-foreground">
                  You've successfully joined as a {invitation.role?.toLowerCase()}.
                </p>
              </div>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate("/")}>
                  Go to Dashboard
                </Button>
                <p className="text-sm text-muted-foreground">
                  Redirecting in {countdown} seconds...
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
